import { css } from '@emotion/react';
import { Close } from '@mui/icons-material';
import { Button, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import QRCode from 'react-qr-code';

import { createEventAccessCodeQRCode, isProp } from '@jebel/utils';

import { DividerOr } from 'shared/components/symbols';
import { createSVGDownloadableURL } from 'shared/utils/url';

export type EventCodeModalProps = {
  event: string | null | undefined;

  show: boolean;

  onClose?: () => void;
};

export function EventCodeModal({ event: id, show, onClose }: EventCodeModalProps) {
  const [downloadableCodeURL, setDownloadableCodeURL] = useState<string | undefined>(undefined);

  const codeValue = useMemo(() => createEventAccessCodeQRCode({ id }), [id]);

  const handleUpdateCodeRef = useCallback(async (ref: unknown) => {
    if (!isProp(ref, 'nodeType')) {
      // Not a valid SVG element
      return;
    }

    const url = await createSVGDownloadableURL(ref as SVGSVGElement);
    setDownloadableCodeURL(url);
  }, []);

  return (
    <Dialog open={show} onClose={onClose} maxWidth="xs" fullWidth css={modalCSS}>
      <DialogTitle css={headerCSS}>
        <Typography variant="h6">Event Attendance Code</Typography>

        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent css={contentCSS}>
        <QRCode
          value={codeValue}
          size={256}
          css={codeCSS}
          id="event-code-qr-code"
          ref={handleUpdateCodeRef}
        />

        <Typography variant="body2" color="textSecondary" align="center">
          Show this QR code to attendees so they can mark their attendance.
        </Typography>

        <DividerOr />

        <Button
          component="a"
          variant="contained"
          color="primary"
          href={downloadableCodeURL}
          disabled={!downloadableCodeURL}
          download={`${id}-qr-code.png`}
        >
          Download QR Code
        </Button>
      </DialogContent>
    </Dialog>
  );
}

const modalCSS = css`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const headerCSS = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const contentCSS = css`
  display: flex;
  flex-direction: column;
  text-align: center;
  gap: 2rem;
`;

const codeCSS = css`
  min-height: 10rem;
  align-self: center;
`;
